import { ArrowUpward } from '@mui/icons-material';
import { Box, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setTaskSelectedItem, setTaskSortSelectedItem } from 'app/actions/map';
import { Autosizer, Scrollbar } from 'app/components';
import {
  sortItemsTaskListMapSelector,
  taskColumnCheckedItemsMapSelector,
  taskSelectedItemMapSelector,
  taskSortSelectedItemMapSelector,
} from 'app/selectors/map';
import React, { createContext, forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';

const MapFilterTaskListContext = createContext();

const MapFilterTaskList = () => {
  const dispatch = useDispatch();
  const taskSortSelectedItem = useSelector(taskSortSelectedItemMapSelector);
  const taskColumnCheckedItems = useSelector(taskColumnCheckedItemsMapSelector);
  const taskSelectedItem = useSelector(taskSelectedItemMapSelector);
  const sortItems = useSelector(sortItemsTaskListMapSelector);

  const columns = useMemo(() => {
    return config.mapTaskColumns.filter((column) => taskColumnCheckedItems.includes(column.name));
  }, [taskColumnCheckedItems]);
  const handleSortClick = (value) => dispatch(setTaskSortSelectedItem(value));

  const handleItemClick = (value) => dispatch(setTaskSelectedItem(value));

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        '.simplebar-offset > div': {
          '&::-webkit-scrollbar': {
            width: 0,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            boxShadow: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      }}>
      <Autosizer>
        {({ height, width }) => (
          <Box sx={{ height, width }}>
            <MapFilterTaskListContext.Provider
              value={{
                columns: columns,
                onSortClick: handleSortClick,
                sortSelectedItem: taskSortSelectedItem,
              }}>
              <Scrollbar>
                {({ scrollableNodeRef, contentNodeRef }) => (
                  <FixedSizeList
                    height={height}
                    innerElementType={innerElementType}
                    itemCount={sortItems.length}
                    itemSize={24}
                    overscanCount={10}
                    innerRef={contentNodeRef}
                    outerRef={scrollableNodeRef}
                    itemData={{
                      items: sortItems,
                      columns: columns,
                      selectedItem: taskSelectedItem,
                      onClick: handleItemClick,
                    }}>
                    {StickyListItem}
                  </FixedSizeList>
                )}
              </Scrollbar>
            </MapFilterTaskListContext.Provider>
          </Box>
        )}
      </Autosizer>
    </Box>
  );
};

const innerElementType = forwardRef(({ children, style, ...rest }, ref) => (
  <MapFilterTaskListContext.Consumer>
    {({ columns, onSortClick, sortSelectedItem }) => (
      <div ref={ref} style={style} {...rest}>
        <ListItem
          disablePadding
          sx={(theme) => ({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: style.height,
            minHeight: '100%',
            zIndex: '2',
            pointerEvents: 'none',
            padding: theme.spacing(0),
            '.MuiListItemText-root': {
              display: 'flex',
              justifyContent: 'space-between',
              height: '100%',
              position: 'relative',
              flex: 'none',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: '0',
                right: '0',
                height: '100%',
                bgcolor: theme.palette.divider,
                width: '1px',
                zIndex: '1',
                pointerEvents: 'none',
              },
              '&:last-of-type': {
                flex: '1',
                '&:before': {
                  display: 'none',
                },
              },
            },
          })}>
          {columns.map((column) => (
            <ListItemText key={column.name} style={{ width: column.width }} />
          ))}
        </ListItem>
        <ListItem
          disablePadding
          sx={(theme) => ({
            position: 'sticky',
            top: '0',
            width: '100%',
            display: 'flex',
            zIndex: '1',
            '.MuiListItemButton-root': {
              display: 'flex',
              flex: 'none',
              height: '30px',
              padding: theme.spacing(0),
              position: 'relative',
              bgcolor: 'background.default',
              borderColor: theme.palette.divider,
              '&:first-of-type': {
                '.MuiListItemText-root': {
                  paddingLeft: '31px',
                },
              },
              '&:last-of-type': {
                flex: '1',
                '.MuiListItemText-root': {
                  paddingRight: theme.spacing(2),
                },
              },
              '.MuiTouchRipple-rippleVisible': {
                color: 'inherit',
              },
              '.MuiListItemText-root': {
                display: 'flex',
                cursor: 'pointer',
                margin: theme.spacing(0),
                padding: '6px 6px',
                alignItems: 'center',
                justifyContent: 'space-between',
                '.MuiTypography-root': {
                  display: 'flex',
                  '&:nth-of-type(1)': {
                    color: 'text.secondary',
                    marginRight: '4px',
                  },
                  '&:nth-of-type(2) svg': {
                    fontSize: '12px',
                    transition: 'all 300ms',
                    transform: 'rotateZ(0deg)',
                  },
                },
              },
            },
          })}>
          {columns.map((column) => (
            <ListItemButton
              key={column.name}
              onClick={() => onSortClick(column)}
              disableTouchRipple={!column.order}
              sx={(theme) => ({
                minWidth: column.width,
                '.MuiListItemText-root': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  '.MuiTypography-root': {
                    width: 'auto',
                    '&:nth-of-type(2)': {
                      opacity: '0.2',
                      transform: 'rotateZ(0)',
                      transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms',
                      ...(sortSelectedItem.name === column.name && {
                        opacity: '1',
                        ...(sortSelectedItem.order === 'DESC' && {
                          transform: 'rotateZ(180deg)',
                        }),
                      }),
                    },
                  },
                },
              })}>
              <ListItemText disableTypography>
                <Typography>{column.label}</Typography>
                <Typography>{column.order && <ArrowUpward fontSize='small' />}</Typography>
              </ListItemText>
            </ListItemButton>
          ))}
        </ListItem>
        {children}
      </div>
    )}
  </MapFilterTaskListContext.Consumer>
));

const StickyListItem = ({ data, index, style }) => {
  const { items, columns, selectedItem, onClick } = data;

  return (
    <ListItem
      key={index}
      disablePadding
      style={style}
      selected={selectedItem?.id === items[index].id}
      onClick={() => onClick(items[index])}
      sx={(theme) => ({
        width: '100%',
        marginTop: '30px',
        userSelect: 'none',
        borderRadius: '0',
        '&:hover': {
          '.MuiListItemText-root': {
            cursor: 'pointer',
          },
        },
        '&.Mui-selected': {
          '.MuiListItemText-root': {
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          '.MuiCheckbox-root': {
            color: theme.palette.primary.contrastText,
            '&.Mui-checked': {
              color: theme.palette.primary.contrastText,
            },
          },
        },
        '.MuiListItemText-root': {
          display: 'flex',
          flex: 'none',
          height: '100%',
          margin: theme.spacing(0),
          padding: theme.spacing(0.5, 1),
          alignItems: 'center',
          '&:first-of-type': {
            paddingLeft: theme.spacing(2),
            svg: {
              fontSize: '16px',
              marginRight: '3px',
            },
          },
          '&:last-of-type': {
            flex: '1',
            paddingRight: theme.spacing(2),
          },
          '.MuiTypography-root': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          },
          '.MuiCheckbox-root': {
            padding: theme.spacing(0),
            marginLeft: '-2px',
            svg: {
              fontSize: '18px',
            },
          },
        },
        ...((items.length - (items.length - 1 - index)) % 2 === 0 && {
          '& .MuiListItemText-root': {
            bgcolor: 'background.default',
          },
        }),
        ...(items[index].status === 'active' && {
          '& .MuiListItemText-root': {
            color: theme.palette.success.contrastText,
            bgcolor: theme.palette.success.light,
          },
          '&:hover': {
            cursor: 'pointer',
            '& .MuiListItemText-root': {
              bgcolor: theme.palette.success.light,
            },
          },
        }),
        ...(items[index].status === 'finished' && {
          '&:hover': {
            cursor: 'pointer',
          },
        }),
      })}>
      {columns.map((column) => {
        switch (column.name) {
          case 'id':
            return (
              <ListItemText key={column.name} disableTypography style={{ minWidth: column.width }}>
                <Typography>{items[index].id}</Typography>
              </ListItemText>
            );
          case 'status':
            return (
              <ListItemText key={column.name} disableTypography style={{ minWidth: column.width }}>
                <Typography>
                  {
                    {
                      finished: 'Завершено',
                      active: 'Активно',
                    }[items[index].status]
                  }
                </Typography>
              </ListItemText>
            );
          case 'transport_number':
            return (
              <ListItemText key={column.name} disableTypography style={{ minWidth: column.width }}>
                <Typography>
                  {items[index].transport_number}_{moment(items[index].task_date).format('YY.MM.DD HH:mm').replace(/\D/g, '')}
                </Typography>
              </ListItemText>
            );
          case 'task_date':
            return (
              <ListItemText key={column.name} disableTypography style={{ minWidth: column.width }}>
                <Typography>{moment(items[index].task_date).format('DD.MM.YYYY HH:mm')}</Typography>
              </ListItemText>
            );
          case 'finished_date':
            return (
              <ListItemText key={column.name} disableTypography style={{ minWidth: column.width }}>
                <Typography>
                  {moment(items[index].finished_date).isValid() && moment(items[index].finished_date).format('DD.MM.YYYY HH:mm')}
                </Typography>
              </ListItemText>
            );
        }
      })}
    </ListItem>
  );
};

export default MapFilterTaskList;
